import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { ThemeProvider } from "@mui/material/styles";
import Table_theme from "../Themes/TableTheme";
import FormThemes from "../Themes/FormThemes";
import { ReactComponent as DeleteIcon } from "../Icons/Delete.svg";
import { ReactComponent as SettingsIcon } from "../Icons/Settings.svg";
import CloseIcon from "@mui/icons-material/Close";
import MainLayout from "../Themes/MainLayout";
import MuiAlert from "@mui/material/Alert";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { hasPermission } from "../utils/auth";
import { formatDateLocal } from "../utils/dateUtils";
import {
  TextField,
  Tooltip,
  Pagination,
  Grid,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  CssBaseline,
} from "@mui/material";
import {
  Box,
  Button,
  Container,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  InputProps,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Snackbar,
  Alert,
  Popover,
  InputAdornment,
  Switch,
  FormControlLabel,
  Typography,
} from "@mui/material";
import {
  Add,
  Delete,
  Edit,
  Flag,
  FilterList,
  Settings,
  Search,
} from "@mui/icons-material";
import { useCurrentUser } from "../utils/useCurrentUser";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

const empAssetFields = [
  "employee_name",
  "asset_description",
  "model_no",
  "model_name",
  "serial_no",
  "warranty_validity",
  "asset_condition",
];
const emppopupFields = ["employee_id", "asset_id"];
const empfieldLabels = {
  employee_id: "Employee",
  employee_name: "Employee Name",
  asset_id: "Asset",
  asset_description: "Asset Description",
  model_no: "Model No",
  model_name: "Model Name",
  serial_no: "Serial No",
  warranty_validity: "Warranty Validity",
  asset_condition: "Asset Condition",
};
const EmployeeAssets = () => {
  const { loading, userId, empId, boId, roles, hasPermission } = useCurrentUser();
  const bo_id = boId;
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [assetDetails, setAssetDetails] = useState(null);
  const [isEditing, setIsEditing] = useState(false); // Added isEditing state
  const [snackbarSeverity, setSnackbarSeverity] = useState("success"); // or "error"
  const [assets, setAssets] = React.useState([]);
  const [assetName, setAssetName] = useState({});
  const [empName, setEmpName] = useState({});
  const [statusOptions, setStatusOptions] = useState([]);
  const [filters, setFilters] = useState({ deleteFlag: "ACTIVE" });
  const [anchorEls, setAnchorEls] = useState({});
  const [totalPages, setTotalPages] = useState(1);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteAssetId, setDeleteAssetId] = useState(null);
  const [errors, setErrors] = useState({
    asset_id: false,
    employee_id: false,
  });
  const [columnVisibility, setColumnVisibility] = useState(() => {
    const initial = {};
    empAssetFields.forEach((f) => (initial[f] = true));
    return initial;
  });
  const [columnAnchorEl, setColumnAnchorEl] = useState(null);
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [newAsset, setNewAsset] = useState({
    delete_flag: "N",
    data_source: "UI",
    created_by: "system",
    created_on_date: new Date().toISOString().split("T")[0],
  });
  const [empEditAsset, setEditEmpAsset] = useState({});
  const [snackbarMsg, setSnackbarMsg] = useState("");
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [search, setSearch] = useState("");
  const rowsPerPage = 10;  
  const resetForm = () => {
    setNewAsset({
      bo_id,
      created_by: empId,
      modified_by: empId,
      created_on_date: new Date(),
    });
    setErrors({});
    setIsEditing(false);
  };
  const fetchempAssets = useCallback(async () => {
    try {
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        ...filters,
        search: search.trim(),
        bo_id,
        empId,
        roles,
      };
      const response = await axios.get(
        `${BASE_URL}/EmployeeAssets/paginated`,
        { params }
      );
      setAssets(response.data.data);
      setTotalCount(response.data.total);
    } catch (err) {
      console.error("Error fetching employee assets:", err);
    }
  }, [page, filters, search]);

  useEffect(() => {
    fetchempAssets();
  }, [fetchempAssets]);

  useEffect(() => {
    setPage(0);
  }, [search, filters, bo_id]);

  const [empIdOptions, setempIdOptions] = useState([]); // For Employee ID dropdown
  const [assetIdOptions, setassetIdOptions] = useState([]);

  useEffect(() => {
    if (open) {
      axios
        .get(`${BASE_URL}/EmployeeAssets/getAllEmployeeIds/${bo_id}`)
        .then((res) => {
          setempIdOptions(res.data);
        })
        .catch((err) => console.error("Error fetching dropdown options:", err));
      axios
        .get(`${BASE_URL}/EmployeeAssets/getAllAssetIds/${bo_id}`)
        .then((res) => {
          setassetIdOptions(res.data);
        })
        .catch((err) => console.error("Error fetching dropdown options:", err));
    }
  }, [open, bo_id]);

  useEffect(() => {
    const fetchStatusFlags = async () => {
      try {
        const res = await fetch(
          `${BASE_URL}/commonVariables/statusFlag`
        );
        const data = await res.json();
        setStatusOptions(data); // [{ code: "ACTIVE", label: "Active" }, ...]
      } catch (err) {
        console.error("Failed to fetch employee status flags:", err);
      }
    };
    fetchStatusFlags();
  }, []);

  const handleAssetChange = async (event) => {
    const assetId = event.target.value; // directly asset_id now
    try {
      const res = await axios.get(
        `${BASE_URL}/EmployeeAssets/getAssetDetails/${assetId}/${bo_id}`
      );
      const details = res.data;
      setAssetDetails(details);
    } catch (err) {
      console.error("Error fetching asset details:", err);
      setAssetDetails(null);
    }
  };

  const renderField = (
    field,
    state,
    setState,
    isEditMode = false,
    customOnChange
  ) => {
    const handleChange = (e) => {
      const { name, value } = e.target;
      setState((prev) => ({ ...prev, [name]: value }));

      if (customOnChange) {
        customOnChange(e);
      }
    };
    // Always render these fields as dropdown
    const isDropdownField = ["employee_id", "asset_id"].includes(field);
    let options = [];
    if (field === "employee_id") {
      options = empIdOptions;
    }
    if (field === "asset_id") {
      if (!isEditMode) {
        // Add mode: only show assets with status "Available"
        options = assetIdOptions.filter(
          (opt) => opt.asset_status === "Available"
        );
      } else {
        // Edit mode: show all available assets + the currently selected asset
        const currentAssetId = state.asset_id;
        options = assetIdOptions.filter(
          (opt) =>
            opt.asset_status === "Available" || opt.asset_id === currentAssetId
        );
      }
    }
    return (
      <TextField
        select={isDropdownField}
        label={empfieldLabels[field] || field}
        name={field}
        value={state[field] || ""}
        onChange={(e) => {
          handleChange(e);
          // Clear error when user selects something
          setErrors((prev) => ({ ...prev, [field]: false }));
        }}
        required
        error={errors[field]} //  Show red border
        helperText={
          errors[field]
            ? `Please select ${field === "employee_id" ? "Employee" : "Asset"}`
            : ""
        } // Show error text
        size="small"
        margin="dense"
        fullWidth
        InputLabelProps={{ shrink: true }} // forces label above
        sx={{
          fontSize: "12px",
          mb: 1,
          "& .MuiInputBase-input, & .MuiInputLabel-root": {
            fontSize: "14px",
          },
          "& .MuiInputBase-root": {
            fontSize: "12px",
            width: "200px",
          },
          "& .MuiOutlinedInput-root": {
            "&:hover fieldset": { borderColor: "#1976d2" },
            "&.Mui-focused fieldset": { borderColor: "#1976d2" },
            height: "35px",
          },
        }}
      >
        {isDropdownField &&
          options.map((opt) => (
            <MenuItem
              key={opt.employee_id || opt.asset_id}
              value={opt.employee_id || opt.asset_id} // only send the ID to backend
              sx={{ fontSize: "12px" }}
            >
              {opt.employee_id
                ? `${opt.employee_id} - ${opt.employee_name}`
                : `${opt.asset_id} - ${opt.asset_description}`}
            </MenuItem>
          ))}
      </TextField>
    );
  };
  const handleAdd = async () => {
    const newErrors = {
      asset_id: !newAsset.asset_id && !newAsset.assetId,
      employee_id: !newAsset.employee_id,
    };

    setErrors(newErrors);

    // If any field has an error, stop the function
    if (newErrors.asset_id || newErrors.employee_id) {
      return;
    }

    try {
      await axios.post(`${BASE_URL}/EmployeeAssets`, {
        ...newAsset,
        bo_id, // include existing field
        asset_id: newAsset.asset_id || newAsset.assetId,
        created_by: empId, // Add empId as created_by
        modified_by: empId, // Add empId as modified_by
      });
      setAssetDetails(null);
      setOpen(false);
      setSuccessMessage("Record added successfully");
      setTimeout(() => setSuccessMessage(""), 5000);
      fetchempAssets();
    } catch (err) {
      console.error("Add failed:", err.response?.data || err.message);
      setErrorMessage(err.message || "Failed to add record");
      setTimeout(() => setErrorMessage(""), 5000);
    }
  };

  const handleEdit = async () => {
    try {
      const { employee_id, asset_id, emp_asset_id } = empEditAsset;
      await axios.put(`${BASE_URL}/EmployeeAssets`, {
        bo_id,
        employee_id,
        asset_id,
        emp_asset_id,
        modified_by: empId, // Add empId as modified_by
      });
      setEditOpen(false);
      setSuccessMessage("Record updated successfully..");
      setTimeout(() => setSuccessMessage(""), 5000);
      fetchempAssets();
    } catch (err) {
      console.error("Update failed:", err.response?.data || err.message);
      setErrorMessage(err.message || "Update failed..");
      setTimeout(() => setErrorMessage(""), 5000);
    }
  };
  const handleFilterIconClick = (field, event) => {
    setAnchorEls((prev) => ({ ...prev, [field]: event.currentTarget }));
  };
  const handleFilterClose = (field) => {
    setAnchorEls((prev) => ({ ...prev, [field]: null }));
  };
  const handleDelete = (
    emp_asset_id,
    asset_id,
    employee_name,
    asset_description
  ) => {
    setDeleteId(emp_asset_id);
    setDeleteAssetId(asset_id);
    setEmpName(employee_name);
    setAssetName(asset_description);
    setConfirmOpen(true);
  };
  const confirmDelete = async () => {
    try {
      await axios.delete(
        `${BASE_URL}/EmployeeAssets/${deleteId}/${deleteAssetId}/${bo_id}`
      );
      setSuccessMessage("Record Deleted Successfully..");
      setTimeout(() => setSuccessMessage(""), 5000);
      fetchempAssets();
    } catch (err) {
      console.error("Delete failed:", err.response?.data || err.message);
      setErrorMessage(err.message || "Delete failed..");
      setTimeout(() => setErrorMessage(""), 5000);
    } finally {
      setConfirmOpen(false);
      setDeleteId(null);
      setDeleteAssetId(null);
    }
  };
  const handleToggleFlag = async (emp_asset_id, currentFlag) => {
    const newFlag = currentFlag === "N" ? "Y" : "N";
    // Optimistic UI update
    setAssets((prevAssets) =>
      prevAssets.map((asset) =>
        asset.emp_asset_id === emp_asset_id
          ? { ...asset, delete_flag: newFlag }
          : asset
      )
    );
    try {
      const res = await fetch(
        `${BASE_URL}/EmployeeAssets/${emp_asset_id}/${bo_id}/toggle-flag`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ currentFlag }),
        }
      );
      if (!res.ok) {
        const errorText = await res.text();
        console.error("Toggle flag error:", res.status, errorText);
        // revert on error
        setAssets((prevAssets) =>
          prevAssets.map((asset) =>
            asset.emp_asset_id === emp_asset_id
              ? { ...asset, delete_flag: currentFlag }
              : asset
          )
        );
        return;
      }
      const result = await res.json();
      // Update state with confirmed backend value just in case
      setAssets((prevAssets) =>
        prevAssets.map((asset) =>
          asset.emp_asset_id === emp_asset_id
            ? { ...asset, delete_flag: result.newFlag }
            : asset
        )
      );
      setSuccessMessage("Toggle flag updated Successfully..");
      setTimeout(() => setSuccessMessage(""), 5000);
      fetchempAssets();
    } catch (error) {
      console.error("Toggle flag error:", error.message);
      // revert on error
      setAssets((prevAssets) =>
        prevAssets.map((asset) =>
          asset.emp_asset_id === emp_asset_id
            ? { ...asset, delete_flag: currentFlag }
            : asset
        )
      );
      setErrorMessage(error.message || "Delete failed..");
      setTimeout(() => setErrorMessage(""), 5000);
    }
  };
  const handleCloseDialog = () => {
    setAssetDetails(null);
    setNewAsset({});
    setEditEmpAsset({});
    setOpen(false);
    setEditOpen(false);
    setIsEditing(false);
  };
  return (
    <ThemeProvider theme={Table_theme}>
       <MainLayout>
        <Typography
          variant="h5"
          gutterBottom
          fontSize="25px"
          fontWeight="600"
        >
          Employee Assets
        </Typography>
        {successMessage && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {successMessage}
          </Alert>
        )}
        {errorMessage && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorMessage}
          </Alert>
        )}
        {/* <Box
          display="flex"
          justifyContent="flex-start"
          alignItems="center"
          mt={2}
          mb={2}
          gap={2}
        ></Box> */}
        <Box
          display="flex"
          justifyContent="flex-start"
          alignItems="center"
          mt={0.5}
          mb={2}
          gap={2}
        >
          <IconButton onClick={(e) => setColumnAnchorEl(e.currentTarget)}>
            <SettingsIcon />
          </IconButton>
          <Popover
            open={Boolean(columnAnchorEl)}
            anchorEl={columnAnchorEl}
            onClose={() => setColumnAnchorEl(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
            PaperProps={{
              sx: {
                mt: 1,
                p: 1,
                width: 280,
                maxHeight: 300,
                overflowY: "auto",
                borderRadius: 2,
              },
            }}
          >
            {empAssetFields.map((field) => (
              <MenuItem key={field} disableGutters sx={{ p: 0, mb: 1 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    width: "100%",
                    border: "1px solid #ccc",
                    borderRadius: 1.5,
                    px: 2,
                    py: 1,
                    backgroundColor: "#fff",
                  }}
                >
                  <Typography
                   sx={{
                      fontSize: "0.8rem",
                      color: "#333",
                      fontWeight: "bold",
                    }}
                  >
                    {empfieldLabels[field]}
                  </Typography>
                  <Switch
                    size="small"
                    checked={columnVisibility[field]}
                    onChange={() =>
                      setColumnVisibility((prev) => ({
                        ...prev,
                        [field]: !prev[field],
                      }))
                    }
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked": {
                        color: "#2DB0E4",
                      },
                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                      {
                        backgroundColor: "#2DB0E4",
                      },
                    }}
                  />
                </Box>
              </MenuItem>
            ))}
          </Popover>
          {hasPermission("Employee Assets", "Edit") && (
            <Button
              variant="contained"
              size="small"
              onClick={() => {
                setAssetDetails(null);
                setNewAsset({});
                setErrors({});
                setOpen(true);
              }}
              sx={{
                fontWeight: "bold",
                borderRadius: "8px",
                px: 2.5,
                height: 36,
                backgroundColor: "#2DB0E4",
              }}
              startIcon={<Add />}
            >
              Add
            </Button>
          )}
          <TextField
            size="small"
            sx={{
            width: 250,
            "& input": { fontSize: 14 }, // input + placeholder
            "& input::placeholder": { fontSize: 12 }, // placeholder only
          }}
            placeholder="Search..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              sx: {
                height: 36, // Reduce overall input height
                paddingRight: 0,
              },
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          {/* Status Dropdown */}
          <TextField
            select
            size="small"
            label="Status"
            value={filters.deleteFlag}
            onChange={(e) =>
              setFilters((prev) => ({ ...prev, deleteFlag: e.target.value }))
            }
            sx={{
            width: 200,
            // Make height match the 36px of the search field
            "& .MuiInputBase-root": {
              height: 36,
            },

            // Fix vertical alignment of text
            "& .MuiSelect-select": {
              display: "flex",
              alignItems: "center",
              fontSize: 12,
              padding: "0 14px",
            },

            // Label font size
            "& .MuiInputLabel-root": { fontSize: "14px" },
          }}
          SelectProps={{
            MenuProps: {
              PaperProps: {
                sx: {
                  "& .MuiMenuItem-root": {
                    fontSize: "14px",
                    minHeight: "32px",
                  },
                },
              },
            },
          }}
          >
            <MenuItem value="All">All</MenuItem>
            {statusOptions.map((opt) => (
              <MenuItem key={opt.code} value={opt.code}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
        </Box>
        <Box
          sx={{ width: "100%", overflowX: "auto", px: 0, py: 2, mt: "-10px" }}
        >
          <ThemeProvider theme={Table_theme}>
            <CssBaseline />
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        whiteSpace: "nowrap",
                        textAlign: "center",
                        fontWeight: "bold",
                      }}
                    >
                      Actions
                    </TableCell>

                    {empAssetFields
                      .filter((f) => columnVisibility[f])
                      .map((field) => (
                        <TableCell
                          key={field}
                          sx={{
                            whiteSpace: "nowrap",
                            textAlign: "left",
                            verticalAlign: "middle",
                          }}
                        >
                          <Box
                            display="inline-flex"
                            alignItems="center"
                            justifyContent="center"
                            sx={{ gap: 0.5 }}
                          >
                            <strong>{empfieldLabels[field]}</strong>
                            <IconButton
                              size="small"
                              onClick={(e) => handleFilterIconClick(field, e)}
                            >
                              <FilterList fontSize="small" />
                            </IconButton>
                          </Box>

                          <Popover
                            open={Boolean(anchorEls[field])}
                            anchorEl={anchorEls[field]}
                            onClose={() => handleFilterClose(field)}
                            anchorOrigin={{
                              vertical: "bottom",
                              horizontal: "left",
                            }}
                          >
                            <Box
                              p={2}
                              display="flex"
                              flexDirection="column"
                              gap={1}
                              width={200}
                            >
                              <TextField
                                size="small"
                                fullWidth
                                placeholder={`Filter ${empfieldLabels[field]}`}
                                value={filters[field] || ""}
                                onChange={(e) =>
                                  setFilters((prev) => ({
                                    ...prev,
                                    [field]: e.target.value,
                                  }))
                                }
                              />
                              <Box
                                display="flex"
                                justifyContent="space-between"
                              >
                                <Button
                                  variant="contained"
                                  size="small"
                                  onClick={() => handleFilterClose(field)}
                                  sx={{ minWidth: 70 }}
                                >
                                  Apply
                                </Button>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  onClick={() => {
                                    setFilters((prev) => ({
                                      ...prev,
                                      [field]: "",
                                    }));
                                    handleFilterClose(field);
                                  }}
                                  sx={{ minWidth: 70 }}
                                >
                                  Clear
                                </Button>
                              </Box>
                            </Box>
                          </Popover>
                        </TableCell>
                      ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {assets.map((row, i) => (
                    <TableRow key={i}>
                      <TableCell
                        sx={{
                          whiteSpace: "nowrap",
                          textAlign: "center",
                        }}
                      >
                        <Box display="flex" gap={0.5} justifyContent="center">
                          <Tooltip
                            title={
                              hasPermission("Employee Assets", "Edit")
                                ? "Edit"
                                : "No permission"
                            }
                          >
                            <span>
                              <IconButton
                                size="small"
                                onClick={async () => {
                                  hasPermission("Employee Assets", "Edit") &&
                                    setEditEmpAsset({
                                      ...row,
                                      employee_id: row.employee_id,
                                      asset_id: row.asset_id, // Explicitly set asset_id
                                      emp_asset_id: row.emp_asset_id,
                                    });
                                  setIsEditing(true);                                  
                                  const currentAssetId = row.asset_id;                                  
                                  try {
                                    // Wait for handleAssetChange to complete
                                    await handleAssetChange({
                                      target: { value: currentAssetId },
                                    });
                                    setEditOpen(true); // Open dialog only after handleAssetChange completes
                                  } catch (err) {
                                    console.error(
                                      "Error in handleAssetChange:",
                                      err
                                    );
                                    setSnackbarMsg(
                                      "Failed to load asset details"
                                    );
                                    setSnackbarSeverity("error");
                                    setShowSnackbar(true);
                                  }
                                }}
                                disabled={!hasPermission("Employee Assets", "Edit")}
                                sx={{ color: "#2DB0E4" }}
                              >
                                {" "}
                                <EditOutlinedIcon sx={{ fontSize: "16px" }} />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip
                            title={
                              hasPermission("Employee Assets", "Delete")
                                ? "Delete"
                                : "No permission"
                            }
                          >
                            <span>
                              <IconButton
                                size="small"
                                onClick={() =>
                                  hasPermission("Employee Assets", "Delete") &&
                                  handleDelete(
                                    row.emp_asset_id,
                                    row.asset_id,
                                    row.employee_name,
                                    row.asset_description
                                  )
                                }
                                disabled={
                                  !hasPermission("Employee Assets", "Delete")
                                }
                                sx={{
                                  color: hasPermission(
                                    "Employee Assets",
                                    "Delete"
                                  )
                                    ? "#fc0404ff"
                                    : "#ccc",
                                }}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip
                            title={
                              hasPermission("Employee Assets", "Edit")
                                ? "Toggle Delete Flag"
                                : "No permission"
                            }
                          >
                            <span>
                              <IconButton
                                size="small"
                                onClick={() =>
                                  hasPermission("Clients", "Edit") &&
                                  handleToggleFlag(
                                    row.emp_asset_id,
                                    row.delete_flag
                                  )
                                }
                                disabled={
                                  !hasPermission("Employee Assets", "Edit")
                                }
                              >
                                <Flag
                                  sx={{
                                    color:
                                      row.delete_flag === "N"
                                        ? "#2E9F50"
                                        : "#FF0C0C",
                                    width: 17,
                                    height: 17,
                                    transition: "color 0.2s",
                                    verticalAlign: "middle",
                                  }}
                                />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      </TableCell>

                      {empAssetFields
                        .filter((f) => columnVisibility[f])
                        .map((field) => (
                          <TableCell
                            key={field}
                            align="left"
                            sx={{
                              whiteSpace: "nowrap",
                              verticalAlign: "middle",
                            }}
                          >
                            {field === "warranty_validity"
                              ? formatDateLocal(row[field])
                              : row[field]}
                          </TableCell>
                        ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Box display="flex" justifyContent="center" mt={2}>
              <Pagination
                count={Math.ceil(totalCount / rowsPerPage)}
                page={page + 1} // Add +1 to match MUI pagination (1-based)
                onChange={(_, newPage) => setPage(newPage - 1)} // Subtract 1 to set back to 0-based index
                color="primary"
                variant="outlined"
                shape="rounded"
                sx={{
                  "& .MuiPaginationItem-root": {
                    borderRadius: "8px",
                    minWidth: "32px",
                    height: "32px",
                    fontSize: "14px",
                  },
                  "& .Mui-selected": {
                    backgroundColor: "#b3dbea !important",
                    color: "black",
                    fontWeight: "bold",
                  },
                  "& .MuiPaginationItem-ellipsis": {
                    display: "none",
                  },
                }}
              />
            </Box>
          </ThemeProvider>
        </Box>
        <ThemeProvider theme={FormThemes}>
          <CssBaseline />
          <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
            <DialogTitle>Confirm Deletion</DialogTitle>
            <DialogContent>
              {`Are you sure you want to permanently delete ${assetName} for the
              employee ${empName}?`}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
              <Button onClick={confirmDelete} color="error" variant="contained">
                Delete
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog
            open={open || editOpen}
            onClose={() => {
              setOpen(false);
              setEditOpen(false);
              resetForm();
            }}
            sx={{
              "& .MuiDialog-paper": {
                borderRadius: 5,
                overflow: "visible",
                width: "100%",
                maxWidth: { xs: "95vw", sm: "400px", md: "500px" },
              },
            }}
          >
            <DialogTitle
              sx={{
                borderRadius: "12px",
                bgcolor: "#f5f5f5",
                p: 1,
                fontSize: "16px",
                fontWeight: 600,
              }}
            >
              {isEditing ? "Update Employee Asset" : "Add Employee Asset"}
              <IconButton
                aria-label="close"
                onClick={handleCloseDialog}
                sx={{ position: "absolute", right: 8, top: 5 }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent
              dividers
              sx={{
                p: "18px 24px",
                display: "flex",
                flexDirection: "column",
                alignItems: "stretch",
              }}
            >
              <Grid container spacing={2} justifyContent="center">
                {/* Original fields */}
                {emppopupFields.map((field, index) => (
                  <Grid item xs={6}>
                    {field === "asset_id"
                      ? renderField(
                        field,
                        isEditing ? empEditAsset : newAsset,
                        isEditing ? setEditEmpAsset : setNewAsset,
                        isEditing,
                        handleAssetChange
                      )
                      : renderField(
                        field,
                        isEditing ? empEditAsset : newAsset,
                        isEditing ? setEditEmpAsset : setNewAsset,
                        isEditing
                      )}
                  </Grid>
                ))}
              </Grid>
              <Grid container spacing={2} justifyContent="center">
                {(isEditing || assetDetails) && (
                  <>
                    {/* Row 1 */}
                    <Grid item xs={6}>
                      <TextField
                        label="Model No"
                        value={assetDetails?.model_no || ""}
                        fullWidth
                        size="small"
                        sx={{ width: "200px" }}
                        InputLabelProps={{ shrink: true }} // forces label above
                        disabled
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        label="Model Name"
                        value={assetDetails?.model_name || ""}
                        fullWidth
                        size="small"
                        sx={{ width: "200px" }}
                        InputLabelProps={{ shrink: true }} // forces label above
                        disabled
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        label="Serial No"
                        value={assetDetails?.serial_no || ""}
                        fullWidth
                        size="small"
                        sx={{ width: "200px" }}
                        disabled
                          InputLabelProps={{ shrink: true }} // forces label above
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        label="Warranty Validity"
                        value={assetDetails?.warranty_validity ?? ""}
                        fullWidth
                        size="small"
                        sx={{ width: "200px" }}
                          InputLabelProps={{ shrink: true }} // forces label above
                        disabled
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        label="Condition"
                        value={assetDetails?.asset_condition || ""}
                        fullWidth
                        size="small"
                        sx={{ width: "200px" }}
                          InputLabelProps={{ shrink: true }} // forces label above
                       disabled
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        label="Warranty Status"
                        value={assetDetails?.warranty_status || ""}
                        fullWidth
                        size="small"
                        sx={{ width: "200px" }}
                          InputLabelProps={{ shrink: true }} // forces label above
                        disabled
                      />
                    </Grid>
                  </>
                )}
              </Grid>
            </DialogContent>
            <DialogActions sx={{ p: "8px 24px", justifyContent: "flex-end" }}>
              <Button
                onClick={handleCloseDialog}
                variant="outlined"
                color="inherit"
                sx={{ mr: 1, color: "#555", borderColor: "#ccc" }}
              >
                Cancel
              </Button>
              <Button
                onClick={isEditing ? handleEdit : handleAdd}
                variant="contained"
                color="primary"
                sx={{
                  fontWeight: "bold",
                  borderRadius: "8px",
                  px: 2.5,
                  height: 36,
                  ...(isEditing
                    ? {
                      backgroundColor: "#4CAF50",
                      "&:hover": { backgroundColor: "#45a049" },
                    }
                    : {
                      backgroundColor: "#2DB0E4",
                      "&:hover": { backgroundColor: "#1D87B0" },
                    }),
                }}
              >
                {isEditing ? "Update" : "Save"}
              </Button>
            </DialogActions>
          </Dialog>
        </ThemeProvider>
     </MainLayout>
    </ThemeProvider>
  );
};
export default EmployeeAssets;
